import { IFilterModel, IColumnConfig } from '@/types/admin/dataTable';
import { ITaskFilterModel, TASK_STATUS_CONFIG, TASK_SORT_OPTIONS, CREATOR_OPTIONS, TaskViewType, TASK_VIEW_TYPES, ITaskTableRow } from '@/types/admin/tasks';
import { ADMIN_COLORS } from '@/constants/admin';

// Convert domain-specific task filters → generic filter model for TableFilters
export const toGenericTaskFilterModel = (f: ITaskFilterModel): IFilterModel => {
  const items: IFilterModel['items'] = [] as any;
  if (Array.isArray(f.assignedTo) && f.assignedTo.length) {
    items.push(...f.assignedTo.map((v: string) => ({ field: 'assignedTo', operator: 'is', value: v })));
  }
  if (Array.isArray(f.assignedToNot) && f.assignedToNot.length) {
    items.push(...f.assignedToNot.map((v: string) => ({ field: 'assignedTo', operator: 'isNot', value: v })));
  }
  if (Array.isArray(f.creator) && f.creator.length) {
    items.push(...f.creator.map((v: string) => ({ field: 'creator', operator: 'is', value: v })));
  }
  if (Array.isArray(f.creatorNot) && f.creatorNot.length) {
    items.push(...f.creatorNot.map((v: string) => ({ field: 'creator', operator: 'isNot', value: v })));
  }
  if (Array.isArray(f.status) && f.status.length) {
    items.push(...f.status.map((v: keyof typeof TASK_STATUS_CONFIG) => ({ field: 'status', operator: 'is', value: v })));
  }
  if (Array.isArray(f.statusNot) && f.statusNot.length) {
    items.push(...f.statusNot.map((v: keyof typeof TASK_STATUS_CONFIG) => ({ field: 'status', operator: 'isNot', value: v })));
  }
  return { items } as IFilterModel;
};

// Convert generic filter model (TableFilters) → domain-specific task filters
export const toDomainTaskFilterModel = (m: IFilterModel, prev: ITaskFilterModel): ITaskFilterModel => {
  const filterItems = (m.items || []);
  const includes = (field: string) => filterItems.filter(i => i.field === field && i.operator !== 'isNot').map(i => i.value as string);
  const excludes = (field: string) => filterItems.filter(i => i.field === field && i.operator === 'isNot').map(i => i.value as string);

  const assignedTo = includes('assignedTo');
  const assignedToNot = excludes('assignedTo');
  const creator = includes('creator');
  const creatorNot = excludes('creator');
  const status = includes('status') as any;
  const statusNot = excludes('status') as any;

  return {
    ...prev,
    assignedTo: assignedTo.length ? assignedTo : undefined,
    assignedToNot: assignedToNot.length ? assignedToNot : undefined,
    creator: creator.length ? creator : [],
    creatorNot: creatorNot.length ? creatorNot : undefined,
    status: status.length ? status : undefined,
    statusNot: statusNot.length ? statusNot : undefined,
  } as ITaskFilterModel;
};

export const getStatusColorHex = (statusValue: keyof typeof TASK_STATUS_CONFIG): string => {
  const cfg = (TASK_STATUS_CONFIG as any)[statusValue as keyof typeof TASK_STATUS_CONFIG];
  const color = cfg?.color as string | undefined;
  switch (color) {
    case 'blue':
      return '#3B82F6';
    case 'yellow':
      return '#F59E0B';
    case 'green':
      return '#10B981';
    case 'red':
      return '#EF4444';
    case 'gray':
    default:
      return ADMIN_COLORS.TEXT_SECONDARY;
  }
};

// Build shared columns (filters + sorts) for Tasks table/filters bars
export const buildTaskTableColumns = (
  view: TaskViewType | string,
  uniqueAssignees: string[],
): IColumnConfig<ITaskTableRow>[] => {
  const isArchived = (String(view) === TASK_VIEW_TYPES.ARCHIVED || String(view) === 'archived');

  const filterColumns: IColumnConfig<ITaskTableRow>[] = [
    {
      key: 'assignedTo', // virtual filter key
      label: 'Assignee',
      filterType: 'multiSelect',
      filterable: true,
      sortable: false,
      filterOptions: [
        { label: 'Unassigned', value: 'unassigned' },
        ...uniqueAssignees.map(a => ({ label: a, value: a })),
      ],
    },
    {
      key: 'creator', // virtual filter key
      label: 'Created By',
      filterType: 'multiSelect',
      filterable: true,
      sortable: false,
      filterOptions: CREATOR_OPTIONS.map(o => ({ label: o.label, value: o.value })),
    },
  ];

  // Only show status filter in list (active) view
  if (!isArchived) {
    filterColumns.push({
      key: 'status',
      label: 'Status',
      filterType: 'multiSelect',
      filterable: true,
      sortable: false,
      filterOptions: Object.entries(TASK_STATUS_CONFIG).map(([k, cfg]) => ({ label: (cfg as any).label, value: k })),
    } as unknown as IColumnConfig<ITaskTableRow>);
  }

  const sortOptions = (isArchived ? TASK_SORT_OPTIONS : TASK_SORT_OPTIONS.filter(opt => opt.value !== 'newestArchiveDate'))
    .map(opt => ({ key: opt.value as string, label: opt.label, sortable: true, filterable: false, defaultSort: (opt.order?.[0] as any) || 'desc' } as IColumnConfig<ITaskTableRow>));

  return [...filterColumns, ...sortOptions];
};

// Shared check for whether any filters are active
export const hasActiveTaskFilters = (
  filterModel: ITaskFilterModel,
  searchQuery: string,
  view: TaskViewType | string,
): boolean => {
  const isArchived = (String(view) === TASK_VIEW_TYPES.ARCHIVED || String(view) === 'archived');
  const hasSearch = (searchQuery?.trim()?.length ?? 0) > 0;
  const hasAssignee = !!filterModel.assignedTo && (filterModel.assignedTo.length > 0);
  const hasCreator = !!filterModel.creator && (filterModel.creator.length > 0);
  const hasStatus = !isArchived && !!(filterModel as any).status && ((filterModel as any).status.length > 0);
  return hasSearch || hasAssignee || hasCreator || hasStatus;
};


